import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, TagDesignTokens } from '../types';
import { buildBoxShadow } from './common';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

export const getTagTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
  transitionDuration: number,
  transitionEasing: string,
): TagDesignTokens => ({
  container: {
    _: {
      position: 'relative',
      display: 'flex',
      'align-items': 'center',
      cursor: 'pointer',
      'padding-top': px(theme.spacing.XXS),
      'padding-bottom': px(theme.spacing.XXS),
      'padding-left': px(theme.spacing.XS),
      'padding-right': px(theme.spacing.XS),
      'background-color': theme.tokens.colors.background.neutral.inactive.value,
      color: theme.tokens.colors.text.primary.value,
      'border-width': px(1),
      'border-style': 'solid',
      'border-color': theme.tokens.colors.background.neutral.inactive.value,
      'border-radius': px(theme.borderRadius.pill),
      'font-family': theme.typography.bodyMediumBold.fontFamily,
      'font-weight': String(theme.typography.bodyMediumBold.fontWeight),
      'font-size': theme.typography.bodyMediumBold.fontSize,
      'line-height': theme.typography.bodyMediumBold.lineHeight,
      transition: `box-shadow ${transitionDuration}ms ${transitionEasing}, border-color ${transitionDuration}ms ${transitionEasing}`,
      '&:hover': {
        'border-color': theme.tokens.colors.border.hover.value,
        'box-shadow': theme.halos.hover,
      },
      '&:active': {
        'border-color': theme.tokens.colors.border.pressed.value,
        'box-shadow': theme.halos.active,
      },
      '&:focus': {
        'border-color': theme.tokens.colors.border.neutral.focused.value,
        'box-shadow': theme.halos.focus,
      },
    },
    selected: {
      'background-color': theme.tokens.colors.background.selected.subtle.value,
      'border-color': theme.tokens.colors.border.selected.subtle.value,
    },
    disabled: {
      color: theme.tokens.colors.text.inactive.value,
      cursor: 'not-allowed',
      'box-shadow': 'none',
      '&:hover': { 'box-shadow': 'none' },
      '&:active': { 'box-shadow': 'none' },
      '&:focus': { 'box-shadow': 'none' },
    },
    disabledUnselected: {
      'border-color': theme.tokens.colors.border.inactive.value,
    },
  },
  closeButton: {
    'margin-left': px(theme.spacing.XS),
  },
});

export const getTagTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): TagDesignTokens => {
  const tag = tokens?.component?.tag;

  const bgTransitionProp = tag?.['transition-property']?.['background-color'];
  const boxShadowTransitionProp = tag?.['transition-property']?.['box-shadow'];
  const transitionDur = tag?.['transition-duration'];
  const transitionFn = tag?.['transition-timing-function'];
  const transition =
    bgTransitionProp && boxShadowTransitionProp && transitionDur && transitionFn
      ? `${bgTransitionProp} ${transitionDur} ${transitionFn}, ${boxShadowTransitionProp} ${transitionDur} ${transitionFn}`
      : undefined;

  return {
    container: {
      _: {
        position: 'relative',
        display: 'flex',
        'align-items': 'center',
        cursor: 'pointer',
        height: tag?.height,
        'padding-top': tag?.label?.['padding-top'],
        'padding-bottom': tag?.label?.['padding-bottom'],
        'padding-left': tag?.label?.['padding-left'],
        'padding-right': tag?.label?.['padding-right'],
        'background-color': tag?.['background-color']?.default,
        color: tag?.['text-color'],
        'border-width': px(1),
        'border-style': 'solid',
        'border-color': 'transparent',
        'border-radius': tag?.['border-radius'],
        'font-family': fontFamily,
        'font-weight': tag?.['font-weight'],
        'font-size': tag?.['font-size'],
        'line-height': tag?.['line-height'],
        transition,
        '&:hover': {
          'background-color': tag?.['background-color']?.hover,
        },
        '&:active': {
          'background-color': tag?.['background-color']?.pressed,
        },
        '&:focus': {
          'box-shadow': tag?.['box-shadow-color']?.focus
            ? buildBoxShadow(tag, 'focus')
            : undefined,
        },
      },
      selected: {
        'background-color': tag?.['background-color']?.selected?.default,
        'border-width': tag?.['border-width']?.selected,
        'border-style': tag?.['border-style']?.selected,
        'border-color': tag?.['border-color']?.selected,
        '&:hover': {
          'background-color': tag?.['background-color']?.selected?.hover,
        },
        '&:active': {
          'background-color': tag?.['background-color']?.selected?.pressed,
        },
      },
      disabled: {
        cursor: 'not-allowed',
        opacity: tag?.opacity?.disabled,
        '&:hover': {},
        '&:active': {},
        '&:focus': {},
      },
      /** Weave dims the disabled state uniformly via `opacity`, so no separate border-color override is needed here. */
      disabledUnselected: undefined,
    },
    closeButton: {
      'margin-left': tag?.icon?.['padding-right'],
    },
  };
};
